import _ from 'lodash';
import React from 'react';

import {Trans} from '../../libs/shared/core/src/helpers/internationalization/react';
import {
  CalendarEventVersionReminder,
  CalendarReminderTypesEnum,
} from '../../src/models/api/calendarEventVersionModel';

export enum CalendarReminderUnitsEnum {
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
}

export interface CalendarReminderValue {
  unit: CalendarReminderUnitsEnum;
  count: number;
}

const calendarReminderUnitToMinutes = {
  [CalendarReminderUnitsEnum.MINUTE]: 1,
  [CalendarReminderUnitsEnum.HOUR]: 60,
  [CalendarReminderUnitsEnum.DAY]: 60 * 24,
  [CalendarReminderUnitsEnum.WEEK]: 60 * 24 * 7,
};

export function buildReminderValueForCalendarEventVersionReminder(reminder: CalendarEventVersionReminder) {
  const {minutesBefore} = reminder;

  // Handle the 0 minutes before edge case.
  if (minutesBefore === 0) {
    return {
      count: 0,
      unit: CalendarReminderUnitsEnum.MINUTE,
    };
  }

  const descendingUnits = [
    CalendarReminderUnitsEnum.WEEK,
    CalendarReminderUnitsEnum.DAY,
    CalendarReminderUnitsEnum.HOUR,
  ];
  const reminderValueUnit = descendingUnits.find(
    (unit) => minutesBefore % calendarReminderUnitToMinutes[unit] === 0,
  );

  return {
    count: reminderValueUnit
      ? minutesBefore / calendarReminderUnitToMinutes[reminderValueUnit]
      : minutesBefore,
    unit: reminderValueUnit || CalendarReminderUnitsEnum.MINUTE,
  };
}

export function buildCalendarEventVersionRemindersForValues(values: ReadonlyArray<CalendarReminderValue>) {
  return _(values)
    .map((v) => ({
      type: CalendarReminderTypesEnum.POPUP,
      minutesBefore: getMinutesBefore(v),
    }))
    .filter(
      (r) =>
        r.minutesBefore >= calendarReminderUnitToMinMaxValues[CalendarReminderUnitsEnum.MINUTE].min &&
        r.minutesBefore <= calendarReminderUnitToMinMaxValues[CalendarReminderUnitsEnum.MINUTE].max,
    )
    .uniqBy((r) => r.minutesBefore)
    .value();
}

function getMinutesBefore(value: CalendarReminderValue) {
  const {count, unit} = value;
  return calendarReminderUnitToMinutes[unit] * count;
}

// We currently support reminders up to 4 weeks before the meeting starts.
export const calendarReminderUnitToMinMaxValues = {
  [CalendarReminderUnitsEnum.MINUTE]: {
    min: 0,
    max: 40320,
  },
  [CalendarReminderUnitsEnum.HOUR]: {
    min: 0,
    max: 672,
  },
  [CalendarReminderUnitsEnum.DAY]: {
    min: 0,
    max: 28,
  },
  [CalendarReminderUnitsEnum.WEEK]: {
    min: 0,
    max: 4,
  },
};

export const calendarReminderUnitToText = {
  [CalendarReminderUnitsEnum.WEEK]: <Trans id="calendar-reminder-unit-weeks">weeks</Trans>,
  [CalendarReminderUnitsEnum.DAY]: <Trans id="calendar-reminder-unit-days">days</Trans>,
  [CalendarReminderUnitsEnum.HOUR]: <Trans id="calendar-reminder-unit-hours">hours</Trans>,
  [CalendarReminderUnitsEnum.MINUTE]: <Trans id="calendar-reminder-unit-minutes">minutes</Trans>,
};
